/**
 * The `open_page` client-side tool: navigate the user's browser to a page in the MDOTM
 * platform (a portfolio's details, the universe list, the outlook page, …).
 *
 * Like `plot_chart` and the choice tools, this is NOT executed against the MCP server. It
 * is advertised to the model by `SphereAIChatSection` (merged into the transport's tool
 * list); the model calls it with a `page` key (plus any required ids), the call is
 * short-circuited in `onToolCall` — which pushes the resolved URL onto the router history —
 * and then acked so the agent loop resumes and the model can confirm to the user.
 *
 * The set of navigable pages is a curated allowlist ({@link PAGES}) mapped onto the
 * type-safe route definitions (see {@link typedUrlForRoute}). Keeping it curated — rather
 * than exposing every route — means the model only ever lands on real, user-facing
 * destinations, and the human-readable descriptions double as the tool's usage guidance.
 *
 * The allowlist is narrowed per user by the destination route's own `guard`, so the tool
 * advertises and accepts exactly the pages this user could reach by clicking (see
 * {@link availablePages}).
 */
import { type RouteName, routesMap, typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { PortfolioStudioSettingTabEnum } from "$root/functional-areas/portfolio-studio-settings/tab-enum";
import { type IUser, user$ } from "$root/functional-areas/user";
import { workspaceViewTabs } from "$root/functional-areas/workspace/view-tab-param";
import { PortfolioStudioTab } from "$root/pages/PortfoliosStudio/portfolio-studio-tabs";
import { getPortfolioStudioUrl } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";
import { HmmRegion } from "@eu.mdotm.api/sphere";
import { AS_OF_SEARCH_PARAM, PROPOSAL_SEARCH_PARAM } from "$root/pages/PortfolioDetails/as-of/asOf";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { z } from "zod";
import type { SphereAIChatTool } from "../MCPHttpClient";

export const OPEN_PAGE_TOOL_NAME = "open_page";

/**
 * The fixed `sphere-handle:` ref for the Portfolio Lab page (MDOT-25318). The model writes it
 * whenever a reply is about the Lab, with or without a tool result behind it: the Lab is one session
 * per user, so there is nothing to mint per call. Resolved by toolLinks.ts's `resolveToolLink`.
 */
export const PORTFOLIO_LAB_REF = "portfolio-lab";

/** One navigable parameter of a page (an entity UUID, a region, …). */
type PageParam = {
	name: string;
	description: string;
	required: boolean;
	/** When set, the only accepted values — anything else is rejected with a recoverable error. */
	values?: readonly string[];
};

/** The regions the market regime analysis is published for — "GLOBAL" being the cross-region map. */
const HMM_REGIONS = Object.values(HmmRegion);

/** A curated, navigable destination mapped onto a type-safe route. */
type PageDef = {
	/** What the page shows — surfaced to the model as usage guidance. */
	description: string;
	/** Params the model must (or may) supply; also used to build the schema + description. */
	params: PageParam[];
	/**
	 * The route this destination lands on. Its `guard` — the same one the router enforces on
	 * arrival — is what decides whether the page is offered to the model at all.
	 */
	route: RouteName;
	/** Build the relative URL from the (already-validated) params. */
	toUrl: (params: Record<string, string>) => string;
};

/**
 * Which of a workspace's two tabs to land on. Shared by both workspace destinations: the tab is a
 * search param on the route, so it's a navigable coordinate like any id.
 */
const WORKSPACE_TAB_PARAM: PageParam = {
	name: "tab",
	description: "Which tab to open. Omit to keep the workspace's saved view.",
	required: false,
	values: workspaceViewTabs,
};

/**
 * The market-insights filter coordinates, shared by the Outlook and Outlook focus destinations.
 *
 * They are named after the levels the app context already quotes — asset class / area / sector — so
 * the model can copy the values straight out of the current-screen context into a navigation call
 * instead of translating them. Only the asset class is a closed set; areas and sectors come from the
 * market data itself, which is why they are described rather than enumerated.
 *
 * All three are optional, and deliberately so: both pages validate the combination against their own
 * option tree and fall back to their default market SILENTLY when it doesn't resolve. A guessed area
 * therefore lands the user on a different market with no error to recover from, so omitting a level
 * the model isn't sure of is strictly better than filling it in.
 */
const MARKET_ASSET_CLASS_PARAM: PageParam = {
	name: "assetClass",
	description: "Asset class to show — EQ (equity), FI (fixed income), CO (commodities).",
	required: false,
	values: ["EQ", "FI", "CO"],
};

const MARKET_AREA_PARAM: PageParam = {
	name: "area",
	description:
		'Geography within the asset class, spelled as the page spells it (e.g. "EU", "US", "JP"; "MARKETS" for all equity markets on the Outlook page). Pass one only if it appears in the current-screen context or came from a market-data tool — omit it rather than guessing.',
	required: false,
};

const MARKET_SECTOR_PARAM: PageParam = {
	name: "sector",
	description:
		'Sector within the area, spelled as the page spells it (e.g. "ENERGY", or "ALL_SECTORS" for the area as a whole). Omit rather than guessing.',
	required: false,
};

/**
 * The allowlist of pages the assistant may open. Add an entry here to expose a new
 * destination; the tool description and validation derive from it automatically.
 */
const PAGES = {
	portfolio_list: {
		description: "The user's portfolios — the same destination the app drawer's Portfolios entry opens.",
		params: [],
		// The one destination whose route is not fixed: `getPortfolioStudioUrl` sends a SMART_WORKSPACE
		// user to the default workspace and everyone else to the Portfolios tab, exactly as the drawer's
		// Portfolios entry does. Gated on the tab's route because the two guards differ only by
		// SMART_WORKSPACE, and that is the very service the branch is taken on.
		route: "PortfoliosStudio",
		toUrl: () => getPortfolioStudioUrl(),
	},
	reference_list: {
		description: "The list of references (Portfolio Studio → References tab).",
		params: [],
		route: "PortfoliosStudio",
		toUrl: () => typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.References }),
	},
	universe_list: {
		description: "The list of investment universes (Portfolio Studio → Universes tab).",
		params: [],
		route: "PortfoliosStudio",
		toUrl: () => typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
	},
	market_view_list: {
		description: "The list of market views (Portfolio Studio → Market Views tab).",
		params: [],
		route: "PortfoliosStudio",
		toUrl: () => typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.MarketViews }),
	},
	smart_workspace: {
		description: "The Smart Workspace — a configurable table/chart view over portfolios.",
		params: [WORKSPACE_TAB_PARAM],
		route: "PortfolioStudio/DefaultWorkspace",
		toUrl: (p) => typedUrlForRoute("PortfolioStudio/DefaultWorkspace", { tab: p.tab }),
	},
	market_dashboard: {
		// Described by what it shows rather than as "the dashboard", so the model can tell it from the
		// `outlook` destination: this page takes no filters BECAUSE it shows all three asset classes at
		// once, which is exactly when it is the right landing place.
		description:
			"The Market dashboard: MDOTM's cross-asset overview — equity, fixed income and commodities side by side, with the market commentary. Open this for the whole-market picture; use `outlook` or `asset_class` when the user names one market.",
		params: [],
		route: "MarketDashboard",
		toUrl: () => typedUrlForRoute("MarketDashboard", {}),
	},
	outlook: {
		description: "The Market Outlook page: MDOTM's positioning per asset class and geography.",
		params: [MARKET_ASSET_CLASS_PARAM, MARKET_AREA_PARAM],
		route: "Outlook",
		// The route's search param for the asset class is `filter`; the tool exposes it as `assetClass`
		// to match the vocabulary the app context and the market-data tools use.
		toUrl: (p) => typedUrlForRoute("Outlook", { filter: p.assetClass, area: p.area }),
	},
	asset_class: {
		description: "The Outlook focus page (asset class analysis): one asset class / geography / sector in depth.",
		params: [MARKET_ASSET_CLASS_PARAM, MARKET_AREA_PARAM, MARKET_SECTOR_PARAM],
		route: "AssetClass",
		toUrl: (p) => typedUrlForRoute("AssetClass", { filter: p.assetClass, area: p.area, sector: p.sector }),
	},
	portfolio_details: {
		description: "A single portfolio's details page.",
		params: [{ name: "portfolioUid", description: "UUID of the portfolio to open.", required: true }],
		route: "PortfolioDetails",
		toUrl: (p) => typedUrlForRoute("PortfolioDetails", { portfolioUid: p.portfolioUid, asOf: p[AS_OF_SEARCH_PARAM] }),
	},
	reference_details: {
		description: "A single reference's details page.",
		params: [{ name: "uuid", description: "UUID of the reference to open.", required: true }],
		route: "ReferenceDetails",
		toUrl: (p) => typedUrlForRoute("ReferenceDetails", { uuid: p.uuid }),
	},
	universe_details: {
		description: "A single investment universe's details page.",
		params: [{ name: "universeUuid", description: "UUID of the universe to open.", required: true }],
		route: "UniverseDetails",
		toUrl: (p) => typedUrlForRoute("UniverseDetails", { universeUuid: p.universeUuid }),
	},
	market_view_details: {
		description: "A single market view's details page.",
		params: [{ name: "uuid", description: "UUID of the market view to open.", required: true }],
		route: "MarketViewWorkSpace/View",
		toUrl: (p) => typedUrlForRoute("MarketViewWorkSpace/View", { uuid: p.uuid }),
	},
	benchmark_details: {
		description: "A single benchmark's details page.",
		params: [{ name: "uuid", description: "UUID of the benchmark to open.", required: true }],
		route: "LegacyBenchmark",
		toUrl: (p) => typedUrlForRoute("LegacyBenchmark", { uuid: p.uuid }),
	},
	proposal_details: {
		// A proposal has no id of its own: it's the parent portfolio's PortfolioDetails page with
		// the `proposal` query flag set, which is what puts the view on the proposed composition
		// instead of the current one.
		description: "A portfolio's pending proposal — the portfolio's details page showing the proposed composition.",
		params: [{ name: "portfolioUid", description: "UUID of the portfolio whose proposal to open.", required: true }],
		route: "PortfolioDetails",
		toUrl: (p) =>
			typedUrlForRoute("PortfolioDetails", { portfolioUid: p.portfolioUid, [PROPOSAL_SEARCH_PARAM]: "true" }),
	},
	regime_analysis: {
		description: "The market regime analysis tool for a given region.",
		params: [
			{
				name: "region",
				// Pinned to the enum instead of "get valid regions from context or a tool": there is no
				// tool that lists them, and the context only carries one when the user is ALREADY on a
				// regime page — so the model was left guessing on exactly the call that opens the page.
				description: 'The region to analyse. "GLOBAL" is the cross-region risk map.',
				required: true,
				values: HMM_REGIONS,
			},
		],
		route: "RegimeAnalysisTool",
		toUrl: (p) => typedUrlForRoute("RegimeAnalysisTool", { region: p.region }),
	},
	workspace: {
		description: "A specific Smart Workspace by id. Use smart_workspace for the default one.",
		params: [{ name: "id", description: "UUID of the smart workspace to open.", required: true }, WORKSPACE_TAB_PARAM],
		route: "PortfolioStudio/Workspace",
		toUrl: (p) => typedUrlForRoute("PortfolioStudio/Workspace", { id: p.id, tab: p.tab }),
	},
	portfolio_lab: {
		description:
			"The Portfolio Lab: the what-if workspace, where each column is a scratch copy of a portfolio, proposal, benchmark or reference. One session per user, so it takes no parameters — it opens whatever columns they last left in it.",
		params: [],
		route: "PortfolioLab",
		toUrl: () => typedUrlForRoute("PortfolioLab", {}),
	},
	storyfolio_list: {
		description: "The list of storyfolios / commentary templates (the Commentary builder home).",
		params: [],
		route: "Storyfolio/Studio",
		toUrl: () => typedUrlForRoute("Storyfolio/Studio", {}),
	},
	storyfolio_details: {
		description: "A single storyfolio's details / editor page (a commentary template).",
		params: [{ name: "uuid", description: "UUID of the storyfolio (commentary template) to open.", required: true }],
		route: "Storyfolio/Details",
		toUrl: (p) => typedUrlForRoute("Storyfolio/Details", { uuid: p.uuid }),
	},
	report_builder: {
		description: "The Report builder: the list of report templates.",
		params: [],
		route: "PortfolioStudioSettings",
		toUrl: () =>
			typedUrlForRoute("PortfolioStudioSettings", { tab: PortfolioStudioSettingTabEnum.ReportCustomisation }),
	},
	report_editor: {
		description: "A single report template's editor page.",
		params: [{ name: "reportUid", description: "UUID of the report template to open.", required: true }],
		route: "PortfolioStudioSettings/ReportEditor",
		toUrl: (p) => typedUrlForRoute("PortfolioStudioSettings/ReportEditor", { reportUid: p.reportUid }),
	},
} satisfies Record<string, PageDef>;

/** The stable page keys advertised to the model. */
export type PageKey = keyof typeof PAGES;

/**
 * The pages this user can actually reach, in allowlist order — the destination route's own guard,
 * evaluated before the navigation instead of after it.
 *
 * Both halves of the tool go through here. A page the user has no service for is neither advertised
 * nor accepted: pushed anyway, the router's guard would bounce it to the index, which the model
 * reports to the user as a successful navigation to a page they never asked for.
 */
function availablePages(user: IUser): PageKey[] {
	return (Object.keys(PAGES) as PageKey[]).filter((key) => routesMap[PAGES[key].route].guard({ user }).passed);
}

/**
 * The kinds of Sphere entity a tool result's `rel: "page"` link can name — sphere-mcp's copy of
 * this union, kept in sync by hand (see toolResultStore.ts's `ToolLink`).
 */
export type LinkEntityType =
	| "investment"
	| "reference"
	| "benchmark"
	| "universe"
	| "market_view"
	| "proposal"
	| "portfolio_lab"
	| "workspace"
	| "default_workspace";

/**
 * Which {@link PAGES} entry shows an entity type's details page, and the param its id fills. An
 * entry with no `param` ignores the id: the Portfolio Lab is one session per user, so a Lab link
 * opens the page itself, and the default workspace has a page of its own. `asOfParam` names the
 * search param that pins the page to a past day, on the one page that has an as-of view; a link's
 * `asOf` is dropped on every other page.
 */
const ENTITY_PAGE: Record<LinkEntityType, { page: PageKey; param?: string; asOfParam?: string }> = {
	investment: { page: "portfolio_details", param: "portfolioUid", asOfParam: AS_OF_SEARCH_PARAM },
	reference: { page: "reference_details", param: "uuid" },
	benchmark: { page: "benchmark_details", param: "uuid" },
	universe: { page: "universe_details", param: "universeUuid" },
	market_view: { page: "market_view_details", param: "uuid" },
	proposal: { page: "proposal_details", param: "portfolioUid" },
	portfolio_lab: { page: "portfolio_lab" },
	workspace: { page: "workspace", param: "id" },
	// The built-in "Portfolios" workspace opens on the default-workspace page, like the drawer's entry.
	default_workspace: { page: "smart_workspace" },
};

/**
 * Whether a value is one of {@link LinkEntityType} — the check `resolveToolLink` needs before it
 * can call {@link entityPageUrl}, since a `ToolLink`'s `entityType` arrives as a plain string out
 * of a store that never interpreted it.
 */
export function isLinkEntityType(value: string): value is LinkEntityType {
	return Object.hasOwn(ENTITY_PAGE, value);
}

/**
 * The relative URL for a tool-linked entity's details page, or `undefined` when this user's own
 * entitlements fail the destination route's guard.
 *
 * Resolved live, at render time, against the current `user$` — the same guard `availablePages`
 * filters the open_page tool by — rather than once when the link arrived: a page link survives a
 * History reopen, potentially in a different session, where a stale answer would be wrong in
 * either direction. `undefined` is not an error: it is what tells the caller (toolLinks.ts's
 * `resolveToolLink`) to fall back to plain text instead of a link the user can't open.
 */
export function entityPageUrl(entityType: LinkEntityType, entityId: string, asOf?: string): string | undefined {
	const { page, param, asOfParam } = ENTITY_PAGE[entityType];
	const def = PAGES[page];
	if (!routesMap[def.route].guard({ user: readAtom(user$) }).passed) return undefined;
	// The page validates the day itself (as-of/asOf.ts `parseAsOfParam`) and drops one it cannot show.
	return def.toUrl({ ...(param ? { [param]: entityId } : {}), ...(asOf && asOfParam ? { [asOfParam]: asOf } : {}) });
}

const openPageSchema = z.object({
	page: z.string().min(1),
	params: z.record(z.string()).optional(),
});

/**
 * Validate a tool-call input as an open-page request. Returns the resolved relative URL and the page
 * key, or a human-readable error the model can recover from (unknown page / a page this user has no
 * access to / missing required param).
 */
export function parseOpenPageSpec(
	input: unknown,
): { ok: true; url: string; page: PageKey } | { ok: false; error: string } {
	const parsed = openPageSchema.safeParse(input);
	if (!parsed.success) {
		const error = parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`).join("; ");
		return { ok: false, error };
	}
	const { page, params = {} } = parsed.data;
	const available = availablePages(readAtom(user$));
	if (!(page in PAGES)) {
		return { ok: false, error: `unknown page "${page}". Valid pages: ${available.join(", ")}.` };
	}
	if (!available.includes(page as PageKey)) {
		// Named as an entitlement rather than as an unknown key, so the model can tell the user their
		// account doesn't include the section instead of retrying the call with a synonym.
		return {
			ok: false,
			error: `the "${page}" page is not available on this account. Valid pages: ${available.join(", ")}.`,
		};
	}
	const def = PAGES[page as PageKey];
	// Widened to PageParam: `satisfies` keeps each entry's literal type, which drops `values` for
	// the pages that don't constrain any param.
	for (const { name, required, values } of def.params as PageParam[]) {
		const value = params[name];
		if (required && !value) {
			return { ok: false, error: `page "${page}" requires the "${name}" param.` };
		}
		// Case-insensitive: these are enum values the model retypes from a description, and the
		// destinations themselves parse them leniently — rejecting "chart" for "CHART" would be a
		// round-trip spent on nothing.
		if (value && values && !values.some((allowed) => allowed.toLowerCase() === value.toLowerCase())) {
			return {
				ok: false,
				error: `invalid "${name}" for page "${page}": "${value}". Valid values: ${values.join(", ")}.`,
			};
		}
	}
	return { ok: true, url: def.toUrl(params), page: page as PageKey };
}

/**
 * The instruction that makes every reply about the Portfolio Lab carry a link to it (MDOT-25318),
 * or `null` when this user cannot open the Lab. SphereAIChatSection sends it with every turn's
 * context, which sphere-mcp places on the last user message — the text the model reads right
 * before it answers.
 */
export function portfolioLabLinkRule(): string | null {
	if (!availablePages(readAtom(user$)).includes("portfolio_lab")) return null;
	return `Whenever your reply acts on, answers about or mentions the Portfolio Lab, include the link [Portfolio Lab](sphere-handle://${PORTFOLIO_LAB_REF}) so the user can open it, without being asked.`;
}

/** One "- key: description [Params: …]" line for a page, for the tool description. */
function pageLine(key: PageKey): string {
	const def: PageDef = PAGES[key];
	// Widened for the same reason as in `parseOpenPageSpec`: `satisfies` keeps each entry's literal
	// type, so `values` is absent from the params of pages that constrain nothing.
	const defParams = def.params as PageParam[];
	const params = defParams.length
		? ` Params: ${defParams
				.map(
					(p) =>
						// The accepted values are advertised here rather than restated in each description:
						// `parseOpenPageSpec` rejects anything outside `values`, so this is the one place that
						// can't drift from what validation enforces.
						`${p.name} (${p.required ? "required" : "optional"}) — ${p.description}${
							p.values ? ` One of: ${p.values.join(", ")}.` : ""
						}`,
				)
				.join("; ")}.`
		: "";
	return `- "${key}": ${def.description}${params}`;
}

/**
 * The tool as advertised to the model. The description enumerates the navigable pages and their
 * params — the only reliable channel for usage guidance (the server builds its own system prompt).
 *
 * Built per request (`getTools` in SphereAIChatSection) off the user's current entitlements, so the
 * enumerated pages and the ones `parseOpenPageSpec` accepts are the same set.
 */
export function buildOpenPageTool(): SphereAIChatTool {
	// Read here rather than taken as an argument: `toUrl` resolves against the same current user (see
	// `portfolio_list`), so a user passed in would only be honoured by half of what the tool does.
	const pages = availablePages(readAtom(user$));
	return {
		name: OPEN_PAGE_TOOL_NAME,
		description:
			"Navigate the user's browser to a page in the MDOTM platform (e.g. a portfolio's details, the " +
			"universe list, the outlook page). Use this when the user asks to open, go to, view, or show a " +
			"specific entity or section. For pages that take an id, you must already know the required UUID — " +
			"take it from the conversation context or fetch it with another tool first; NEVER invent a UUID. " +
			`Available pages:\n${pages.map(pageLine).join("\n")}`,
		inputSchema: {
			type: "object",
			additionalProperties: false,
			properties: {
				page: {
					type: "string",
					enum: pages,
					description: "Which page to open.",
				},
				params: {
					type: "object",
					additionalProperties: { type: "string" },
					description:
						"Params required by the chosen page (e.g. { portfolioUid } for portfolio_details). Omit for pages that take none.",
				},
			},
			required: ["page"],
		},
	};
}
